import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/auth/AuthProvider";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import ProfileHeader from "@/components/profile/ProfileHeader";
import ProfileReviews from "@/components/profile/ProfileReviews";
import PrivateProfileView from "@/components/profile/PrivateProfileView";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import SavedProducts from "@/components/profile/SavedProducts";
import MyReviews from "@/components/profile/MyReviews";

const UserProfile = () => {
  const { username } = useParams<{ username: string }>();
  const { user } = useAuth();
  const { toast } = useToast();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  console.log('Loading profile for username:', username);
  console.log('Current auth user:', user?.id);

  const { data: profile, isLoading: profileLoading, error: profileError } = useQuery({
    queryKey: ['profile', username],
    queryFn: async () => {
      console.log('Fetching profile data for username:', username);
      if (!username) {
        console.error('No username provided');
        throw new Error('No username provided');
      }
      
      const { data: profiles, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('username', username)
        .maybeSingle();

      if (error) {
        console.error('Error fetching profile:', error);
        throw error;
      }
      
      if (!profiles) {
        console.error('Profile not found for username:', username);
        throw new Error('Profile not found');
      }
      
      console.log('Profile data received:', profiles);
      return profiles;
    },
    retry: 1,
    enabled: !!username
  });

  const { data: isFollowing, isLoading: followLoading } = useQuery({
    queryKey: ['following', user?.id, profile?.id],
    queryFn: async () => {
      if (!user?.id || !profile?.id) return false;
      
      const { data, error } = await supabase
        .from('follows')
        .select('id')
        .eq('follower_id', user.id)
        .eq('following_id', profile.id)
        .maybeSingle();

      if (error) {
        console.error('Error checking follow status:', error);
        throw error;
      }
      return !!data;
    },
    enabled: !!user?.id && !!profile?.id && user.id !== profile.id
  });

  const followMutation = useMutation({
    mutationFn: async () => {
      if (!user?.id || !profile?.id) {
        console.log('Follow attempt without auth, redirecting to login');
        navigate('/login');
        return;
      }
      
      const { error } = await supabase
        .from('follows')
        .insert({ follower_id: user.id, following_id: profile.id });

      if (error) {
        console.error('Error following user:', error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['following', user?.id, profile?.id] });
      queryClient.invalidateQueries({ queryKey: ['profile', username] });
      toast({
        title: t('profile.followSuccess'),
        description: t('profile.nowFollowing', { username: profile?.username }),
      });
    }
  });

  const unfollowMutation = useMutation({
    mutationFn: async () => {
      if (!user?.id || !profile?.id) return;
      
      const { error } = await supabase
        .from('follows')
        .delete()
        .eq('follower_id', user.id)
        .eq('following_id', profile.id);

      if (error) {
        console.error('Error unfollowing user:', error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['following', user?.id, profile?.id] });
      queryClient.invalidateQueries({ queryKey: ['profile', username] });
      toast({
        title: t('profile.unfollowSuccess'),
        description: t('profile.noLongerFollowing', { username: profile?.username }),
      });
    }
  });

  const handleFollowAction = () => {
    if (!user) {
      navigate('/login');
      return;
    }

    if (isFollowing) {
      unfollowMutation.mutate();
    } else {
      followMutation.mutate();
    }
  };

  if (profileError) {
    console.error('Profile error:', profileError);
    return (
      <div className="container mx-auto px-4 py-8">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {profileError.message || t('common.error')}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (profileLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center gap-4 mb-6">
            <Skeleton className="w-20 h-20 rounded-full" />
            <div className="space-y-2">
              <Skeleton className="h-6 w-32" />
              <Skeleton className="h-4 w-24" />
            </div>
          </div>
          <div className="space-y-4">
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        </div>
      </div>
    );
  }

  if (!profile) {
    console.error('No profile data available');
    return (
      <div className="container mx-auto px-4 py-8">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {t('profile.userNotFound')}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const isOwnProfile = user?.id === profile.id;
  const canViewProfile = profile.privacy_setting === 'public' || 
                        isOwnProfile || 
                        (profile.privacy_setting === 'contacts' && isFollowing);

  console.log('Profile visibility:', {
    isOwnProfile,
    privacySetting: profile.privacy_setting,
    isFollowing,
    canViewProfile
  });

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-2xl mx-auto">
        <ProfileHeader
          profile={profile}
          isFollowing={isFollowing || false}
          isOwnProfile={isOwnProfile}
          onFollowAction={handleFollowAction}
          followMutationPending={followMutation.isPending}
          unfollowMutationPending={unfollowMutation.isPending}
        />

        {isOwnProfile ? (
          <Tabs defaultValue="reviews" className="mt-8">
            <TabsList className="w-full">
              <TabsTrigger value="reviews">{t('profile.myReviews')}</TabsTrigger>
              <TabsTrigger value="saved">{t('profile.savedProducts')}</TabsTrigger>
            </TabsList>
            <TabsContent value="reviews">
              <MyReviews />
            </TabsContent>
            <TabsContent value="saved">
              <SavedProducts />
            </TabsContent>
          </Tabs>
        ) : canViewProfile ? (
          <ProfileReviews
            reviews={[]}
            isLoading={false}
            isAuthenticated={!!user}
          />
        ) : (
          <PrivateProfileView
            profile={profile}
            isFollowing={isFollowing || false}
            userId={user?.id}
            profileId={profile.id}
            onFollowAction={handleFollowAction}
            followMutationPending={followMutation.isPending}
            unfollowMutationPending={unfollowMutation.isPending}
          />
        )}

        {profile.favorite_brands && profile.favorite_brands.length > 0 && canViewProfile && (
          <div className="mt-8">
            <h2 className="text-xl font-semibold mb-4">{t('profile.favoriteBrands')}</h2>
            <div className="flex flex-wrap gap-2">
              {profile.favorite_brands.map((brand) => (
                <span
                  key={brand}
                  className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-secondary"
                >
                  {brand}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserProfile;